import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import api from "@/lib/api";

/**
 * HomeClientShowcase — a video-first proof section on the homepage.
 * Data source: GET /api/showcase/videos, which returns published showcase
 * entries with a video_url. Admin toggles inclusion via the existing
 * showcase CRUD (homepage_video_featured + video_display_order fields).
 *
 * Playback rules per V1.2.3 spec:
 *  - No autoplay, no auto-loop
 *  - Native controls once the user taps play
 *  - Lazy-loaded (metadata preload only, actual buffering starts on tap)
 *  - Custom thumbnail overlay w/ gold play button until first play
 *  - Fullscreen supported natively via `controls`
 */
const VideoCard = ({ item }) => {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlay = () => {
    setPlaying(true);
    // requestAnimationFrame so the video element has controls attached
    // before we call play() — avoids the "play() interrupted" console noise
    // some browsers throw when the click and play happen in the same tick.
    requestAnimationFrame(() => {
      videoRef.current?.play().catch(() => { /* user can hit native controls */ });
    });
  };

  return (
    <div className="ft-card fx-hover-lift p-0 overflow-hidden flex flex-col" data-testid={`showcase-video-${item.slug}`}>
      <div className="relative aspect-video bg-[var(--ft-black)] group">
        <video
          ref={videoRef}
          src={item.video_url}
          poster={item.hero_image_url || undefined}
          preload="metadata"
          playsInline
          controls={playing}
          className="w-full h-full object-cover"
          data-testid={`showcase-video-el-${item.slug}`}
        />
        {!playing && (
          <button
            type="button"
            onClick={handlePlay}
            aria-label={`Play ${item.brand_name} video`}
            data-testid={`showcase-play-${item.slug}`}
            className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/15 transition-colors"
          >
            <span className="h-16 w-16 md:h-20 md:w-20 rounded-full flex items-center justify-center transition-transform group-hover:scale-105"
                  style={{ background: "var(--ft-gold)", boxShadow: "0 20px 44px -12px rgba(200,162,74,0.55)" }}>
              <Play size={26} strokeWidth={2.2} style={{ color: "var(--ft-black)", marginLeft: 3 }} fill="var(--ft-black)" />
            </span>
          </button>
        )}
      </div>
      <div className="p-6">
        <div className="text-[0.65rem] uppercase tracking-[0.24em] font-semibold text-[var(--ft-gold)] mb-2">
          Client Showcase
        </div>
        <h3 className="text-xl font-bold tracking-tight text-[var(--ft-black)]">{item.brand_name}</h3>
        <div className="ft-gold-rule my-3" />
        <p className="text-sm text-[var(--ft-muted)] leading-relaxed">
          {item.video_description || item.description}
        </p>
      </div>
    </div>
  );
};

const HomeClientShowcase = () => {
  const [videos, setVideos] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/showcase/videos")
      .then((r) => setVideos(r.data.videos || []))
      .catch(() => setVideos([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || videos.length === 0) return null;

  return (
    <section className="py-24 bg-white" data-testid="home-client-showcase">
      <div className="ft-container">
        <div className="max-w-2xl mb-12">
          <div className="ft-eyebrow mb-4">Client Showcase</div>
          <span className="ft-gold-rule-lg" />
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[var(--ft-black)]">
            Real brands, produced through <span className="ft-serif text-[var(--ft-gold)]">FLOWTALIS</span>.
          </h2>
          <p className="mt-4 text-[var(--ft-muted)] leading-relaxed">
            See how businesses are bringing their brands to life with custom private label beverages produced through FLOWTALIS.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6" data-testid="home-showcase-grid">
          {videos.map((v) => <VideoCard key={v.slug} item={v} />)}
        </div>

        <div className="mt-14 text-center max-w-xl mx-auto">
          <h3 className="text-2xl font-bold tracking-tight text-[var(--ft-black)]">
            Ready to showcase your brand <span className="ft-serif text-[var(--ft-gold)]">next</span>?
          </h3>
          <p className="mt-3 text-sm text-[var(--ft-muted)] leading-relaxed">
            Whether you&apos;re launching a new beverage brand or creating custom bottled water for your business or event,
            FLOWTALIS is here to help bring your vision to life.
          </p>
          <Link to="/contact" className="ft-btn-gold mt-6" data-testid="home-showcase-cta">
            Start Your Project <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomeClientShowcase;
